import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Server, Link, MessageSquare, GitBranch, FileText, Send, Mic, MicOff, Paperclip, Home, Menu, X, ChevronRight } from 'lucide-react';
import { investigationOptions } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  plus: Plus,
  server: Server,
  link: Link,
  'message-square': MessageSquare,
  'git-branch': GitBranch,
  'file-text': FileText
};

const Investigator = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [investigationType, setInvestigationType] = useState('general');
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('Speech recognition started');
        finalTranscriptRef.current = inputValue; // Save current input value
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = finalTranscriptRef.current;
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
            finalTranscriptRef.current = finalTranscript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Update input with final + interim
        setInputValue(finalTranscript + interimTranscript);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended');
        // Auto-restart if still supposed to be listening
        if (isListening) {
          try {
            recognition.start();
          } catch (e) {
            console.log('Restart failed:', e);
            setIsListening(false);
          }
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Handle specific error types
        if (event.error === 'network') {
          console.warn('Network error - speech recognition requires internet connection');
        } else if (event.error === 'not-allowed') {
          alert('Microphone access denied. Please allow microphone permissions.');
          setIsListening(false);
        } else if (event.error === 'no-speech') {
          console.log('No speech detected, continuing...');
          return; // Don't stop listening for no-speech errors
        } else {
          setIsListening(false);
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.log('Cleanup error:', e);
        }
      }
    };
  }, [isListening]);

  const toggleListening = async () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      // Stop listening
      if (recognitionRef.current) {
        setIsListening(false);
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error('Error stopping recognition:', e);
        }
      }
    } else {
      // Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Start listening
        if (recognitionRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.error('Failed to start recognition:', e);
            alert('Unable to start voice input. Please try again.');
          }
        }
      } catch (err) {
        console.error('Microphone permission denied:', err);
        alert('Microphone permission denied. Please allow microphone access in your browser settings.');
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize session on mount
    initializeSession();
  }, []);

  const initializeSession = async () => {
    try {
      const response = await axios.post(`${API}/chat/new-session`);
      setSessionId(response.data.session_id);
      
      // Add welcome message
      const welcomeMessage = {
        id: '1',
        role: 'assistant',
        content: "Welcome to RakshakAI. I'm your AI-powered cyber investigator. How can I assist you today?",
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Error initializing session:', error);
    }
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() && sessionId) {
      const userMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: inputValue,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setLoading(true);
      
      try {
        const response = await axios.post(`${API}/chat/investigate`, {
          message: inputValue,
          session_id: sessionId,
          investigation_type: investigationType
        });
        
        const aiMessage = {
          id: response.data.id,
          role: response.data.role,
          content: response.data.content,
          timestamp: response.data.timestamp,
          metadata: response.data.metadata
        };
        
        setMessages(prev => [...prev, aiMessage]);
      } catch (error) {
        console.error('Error sending message:', error);
        const errorMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'I apologize, but I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewInvestigation = async () => {
    setLoading(true);
    await initializeSession();
    setInvestigationType('general');
    setLoading(false);
  };

  const handleInvestigationType = (type, label) => {
    setInvestigationType(type);
    // Add a system message indicating investigation type change
    const typeMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: `Switched to ${label} mode. How can I assist with your ${label.toLowerCase()}?`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, typeMessage]);
  };

  return (
    <div className="h-screen flex bg-black text-white overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${
          sidebarOpen ? 'w-[280px]' : 'md:w-0'
        } fixed md:relative z-50 h-full transition-all duration-300 border-r flex flex-col overflow-hidden`}
        style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-secondary)' }}
      >
        <div className="p-4 border-b" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="flex items-center gap-2 mb-6">
            <span className="text-lg font-semibold">RakshakAI</span>
          </div>
          
          <button
            onClick={handleNewInvestigation}
            disabled={loading}
            className="w-full btn-primary text-sm h-12"
          >
            <Plus className="w-4 h-4" />
            New Investigation
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {[
              { id: 2, label: 'IP Scan', icon: 'server', type: 'ip_scan' },
              { id: 3, label: 'URL Scan', icon: 'link', type: 'url_scan' },
              { id: 4, label: 'Message Analysis', icon: 'message-square', type: 'message_analysis' },
              { id: 5, label: 'Threat Graph', icon: 'git-branch', type: 'general' },
              { id: 6, label: 'Reports', icon: 'file-text', type: 'general' }
            ].map((option) => {
              const IconComponent = iconMap[option.icon];
              return (
                <button
                  key={option.id}
                  onClick={() => handleInvestigationType(option.type, option.label)}
                  className="w-full p-3 flex items-center gap-3 transition-all duration-300 text-left"
                  style={{
                    background: investigationType === option.type ? 'var(--brand-hover)' : 'transparent',
                    color: investigationType === option.type ? 'var(--brand-primary)' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: '0px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--brand-hover)';
                    e.currentTarget.style.color = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    if (investigationType !== option.type) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'var(--text-muted)';
                    }
                  }}
                >
                  {IconComponent && <IconComponent className="w-5 h-5" />}
                  <span className="text-sm">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 border-t space-y-2" style={{ borderColor: 'var(--border-subtle)' }}>
          <button
            onClick={() => navigate('/')}
            className="w-full p-3 flex items-center gap-3 transition-all duration-300 text-left"
            style={{
              background: 'transparent',
              color: 'var(--text-muted)',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--brand-hover)';
              e.currentTarget.style.color = 'var(--brand-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            <Home className="w-5 h-5" />
            <span className="text-sm">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="h-[50px] md:h-[60px] border-b px-3 md:px-6 flex items-center" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-opacity-10 transition-all"
              style={{ background: 'var(--brand-hover)' }}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <h2 className="text-sm md:text-lg font-semibold truncate">Cyber Investigation</h2>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6 space-y-4 md:space-y-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-2 md:gap-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <div
                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 text-xs md:text-sm"
                style={{
                  background: message.role === 'assistant' ? 'var(--brand-primary)' : 'var(--bg-secondary)',
                  color: message.role === 'assistant' ? '#000' : 'var(--brand-primary)'
                }}
              >
                {message.role === 'assistant' ? 'AI' : 'U'}
              </div>
              <div
                className="max-w-[85%] md:max-w-[70%] p-3 md:p-4"
                style={{
                  background: message.role === 'assistant' ? 'var(--bg-secondary)' : 'var(--brand-hover)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '0px'
                }}
              >
                <div 
                  className="body-medium whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: message.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\n/g, '<br/>')
                  }}
                />
                {message.metadata && message.metadata.risk_score && (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Risk Score: <span style={{ color: message.metadata.risk_score > 7 ? '#ff4444' : (message.metadata.risk_score > 4 ? '#ffaa00' : 'var(--brand-primary)') }}>
                        {message.metadata.risk_score}/10
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-2 md:gap-4">
              <div
                className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0 text-xs md:text-sm"
                style={{
                  background: 'var(--brand-primary)',
                  color: '#000'
                }}
              >
                AI
              </div>
              <div
                className="max-w-[85%] md:max-w-[70%] p-3 md:p-4"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '0px'
                }}
              >
                <p className="text-sm md:body-medium">Analyzing...</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3 md:p-6 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="max-w-[900px] mx-auto">
            <div
              className="flex items-end gap-1 md:gap-3 p-2"
              style={{
                background: 'var(--bg-secondary)',
                border: '2px solid var(--border-subtle)',
                borderRadius: '0px'
              }}
            >
              <button
                className="p-2 md:p-3 transition-all duration-300 hidden sm:block"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--brand-primary)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
              >
                <Paperclip className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your query..."
                className="flex-1 bg-transparent outline-none resize-none text-sm md:body-medium py-2 md:py-3"
                style={{ color: 'var(--text-primary)', minHeight: '24px', maxHeight: '120px' }}
                rows={1}
              />
              
              <button
                onClick={toggleListening}
                className={`p-2 md:p-3 transition-all duration-300 flex items-center justify-center`}
                style={{ 
                  color: isListening ? '#ff4444' : 'var(--text-muted)',
                  background: isListening ? 'rgba(255, 68, 68, 0.1)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isListening) {
                    e.currentTarget.style.color = 'var(--brand-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isListening) {
                    e.currentTarget.style.color = 'var(--text-muted)';
                  }
                }}
                title={isListening ? 'Stop listening (click to stop)' : 'Start voice input'}
              >
                {isListening ? <MicOff className="w-4 h-4 md:w-5 md:h-5" /> : <Mic className="w-4 h-4 md:w-5 md:h-5" />}
              </button>
              
              <button
                onClick={handleSendMessage}
                className="p-2 md:p-3 transition-all duration-300"
                style={{
                  background: 'var(--brand-primary)',
                  color: '#000',
                  borderRadius: '0px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--brand-active)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--brand-primary)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <Send className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
            <p className="text-[10px] md:text-xs mt-2 text-center" style={{ color: 'var(--text-muted)' }}>
              RakshakAI can make mistakes. Verify critical information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Investigator;